import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell } from "lucide-react";
import { Drawer, DrawerHeader, DrawerBody, DrawerTitle } from "@/design/components/Drawer";
import { EmptyState } from "@/design/components";
import { formatRelativeShort } from "@/design/format";
import { useNotifications } from "@/domain/useNotifications";
import type { Notification } from "@/domain/types";
import { cn } from "@/design/cn";

export function NotificationsBell() {
  const [open, setOpen] = useState(false);
  const { notifications, unreadCount: unread, markRead, markAllRead } = useNotifications();
  const navigate = useNavigate();

  // Group by calendar day.
  const groups = groupByDay(notifications);

  return (
    <>
      <button
        aria-label={`Notifications${unread ? `, ${unread} unread` : ""}`}
        onClick={() => setOpen(true)}
        className="relative flex h-11 w-11 items-center justify-center rounded-md text-mute transition-colors hover:bg-subtle hover:text-ink"
      >
        <Bell className="h-4 w-4" />
        {unread > 0 && (
          <span className="absolute right-1 top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-brand px-1 text-[10px] font-semibold tabular-nums text-white">
            {unread}
          </span>
        )}
      </button>
      <Drawer open={open} onOpenChange={setOpen} width="420px">
        <DrawerHeader>
          <DrawerTitle className="text-base font-semibold text-ink">Notifications</DrawerTitle>
          <button
            className="mt-1 text-xs font-medium text-brand hover:underline"
            onClick={markAllRead}
          >
            Mark all read
          </button>
        </DrawerHeader>
        <DrawerBody className="px-4">
          {groups.length === 0 && (
            <div className="pt-6">
              <EmptyState title="You are all caught up" body="Approvals, evidence requests and disbursement events will appear here." />
            </div>
          )}
          {groups.map((g) => (
            <div key={g.label} className="mb-4">
              <p className="px-2 pb-1.5 text-[11px] font-semibold uppercase tracking-wide text-faint">
                {g.label}
              </p>
              <ul className="flex flex-col gap-0.5">
                {g.items.map((n) => (
                  <li key={n.id}>
                    <button
                      onClick={() => {
                        markRead(n.id);
                        if (n.href) {
                          setOpen(false);
                          navigate(n.href);
                        }
                      }}
                      className={cn(
                        "flex w-full items-start gap-3 rounded-md p-2.5 text-left transition-colors hover:bg-subtle",
                        !n.read && "bg-brandSoft/60",
                      )}
                    >
                      <span
                        className={cn(
                          "mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full",
                          n.read ? "bg-transparent" : "bg-brand",
                        )}
                        aria-hidden
                      />
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm font-medium text-ink">{n.title}</span>
                        <span className="mt-0.5 block text-xs leading-snug text-mute">{n.body}</span>
                        <span className="data mt-1 block text-[11px] text-faint">
                          {formatRelativeShort(n.timestamp)}
                        </span>
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </DrawerBody>
      </Drawer>
    </>
  );
}

function groupByDay(notifications: Notification[]) {
  const sorted = [...notifications].sort((a, b) => (a.timestamp < b.timestamp ? 1 : -1));
  const map = new Map<string, Notification[]>();
  for (const n of sorted) {
    const label = new Date(n.timestamp).toLocaleDateString(undefined, {
      weekday: "long",
      day: "numeric",
      month: "long",
    });
    if (!map.has(label)) map.set(label, []);
    map.get(label)!.push(n);
  }
  return Array.from(map.entries()).map(([label, items]) => ({ label, items }));
}
